import { HlsAudioPlayer } from '../utils/HlsAudioPlayer';
import React, { useEffect, useState, useCallback, useRef } from 'react';

// Define our component props
interface AudioScrubberProps {
  hlsAudioPlayer: HlsAudioPlayer;
  style?: React.CSSProperties;
}

const AudioScrubber: React.FC<AudioScrubberProps> = ({
  hlsAudioPlayer,
  style,
}) => {
  const [currentTime, setCurrentTime] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const progressBarRef = useRef<HTMLDivElement>(null);
  const [duration, setDuration] = useState(hlsAudioPlayer.getState().duration);

  // Update current time when playing
  useEffect(() => {
    const handleTimeUpdate = (event: Event) => {
      if (!isDragging && event.target instanceof HTMLAudioElement) {
        setCurrentTime(event.target.currentTime);
        setDuration(event.target.duration);
      }
    };

    hlsAudioPlayer.addEventListener('timeupdate', handleTimeUpdate);

    return () => {
      hlsAudioPlayer.removeEventListener('timeupdate', handleTimeUpdate);
    };
  }, [hlsAudioPlayer, isDragging]);

  // Handle click on progress bar
  const handleProgressBarClick = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    if (!progressBarRef.current || duration === 0) return;

    const rect = progressBarRef.current.getBoundingClientRect();
    const position = (event.clientX - rect.left) / rect.width;
    const newTime = Math.max(0, Math.min(duration, position * duration));

    // Update the audio time
    const audioElement = document.querySelector('audio');
    if (audioElement) {
      audioElement.currentTime = newTime;
      setCurrentTime(newTime);
    }
  }, [duration]);

  // Handle drag operations
  const handleDragStart = useCallback(() => {
    setIsDragging(true);
  }, []);

  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleDrag = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    if (!isDragging || !progressBarRef.current || duration === 0) return;

    const rect = progressBarRef.current.getBoundingClientRect();
    const position = Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width));
    const newTime = Math.max(0, Math.min(duration, position * duration));

    setCurrentTime(newTime);

    // Update the audio time
    const audioElement = document.querySelector('audio');
    if (audioElement) {
      audioElement.currentTime = newTime;
    }
  }, [isDragging, duration]);

  // Format time as MM:SS
  const formatTime = (timeInSeconds: number): string => {
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = Math.floor(timeInSeconds % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // Calculate progress percentage
  const progressPercentage = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div style={style}>
      <div className={`flex flex-col space-y-2`}>
        {/* Progress bar */}
        <div
          ref={progressBarRef}
          className="relative h-2 bg-gray-200 rounded cursor-pointer"
          onClick={handleProgressBarClick}
          onMouseDown={handleDragStart}
          onMouseUp={handleDragEnd}
          onMouseLeave={handleDragEnd}
          onMouseMove={handleDrag}
        >
          {/* Background bar */}
          <div
            className="absolute h-full bg-blue-500 rounded transition-all duration-100"
            style={{ width: `${progressPercentage}%` }}
          />
          {/* Scrubber handle */}
          <div
            className="absolute w-3 h-3 -mt-0.5 -ml-1.5 bg-white border-2 border-blue-500 rounded-full transition-all duration-100"
            style={{ left: `${progressPercentage}%` }}
          />
        </div>

        {/* Time display */}
        <div className="flex justify-between text-sm text-gray-600">
          <span>{formatTime(currentTime)}</span>
          <span>{formatTime(duration)}</span>
        </div>
      </div>
    </div>
  );
};

export default AudioScrubber;